// lib/apiError.js
// Eén foutmodel voor alle API-communicatie (roadmap 1B).
// Elke mislukte request wordt een ApiError met een `kind`, zodat UI-code
// 404, auth, validatie, timeout, offline en serverfouten kan onderscheiden
// zonder op message-strings te matchen.

export const ERROR_KINDS = {
  NOT_FOUND: 'not-found',
  AUTH: 'auth',
  VALIDATION: 'validation',
  RATE_LIMIT: 'rate-limit',
  TIMEOUT: 'timeout',
  OFFLINE: 'offline',
  NETWORK: 'network',
  SERVER: 'server',
  UNKNOWN: 'unknown',
};

export class ApiError extends Error {
  constructor(message, { kind = ERROR_KINDS.UNKNOWN, status = null, url = null, cause } = {}) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.url = url;
    if (cause) this.cause = cause;
  }

  // Retryable = opnieuw proberen kan zinvol zijn zonder dat de gebruiker
  // eerst iets anders hoeft te doen
  get retryable() {
    return [
      ERROR_KINDS.TIMEOUT,
      ERROR_KINDS.OFFLINE,
      ERROR_KINDS.NETWORK,
      ERROR_KINDS.SERVER,
      ERROR_KINDS.RATE_LIMIT,
    ].includes(this.kind);
  }
}

export function kindFromStatus(status) {
  if (status === 404) return ERROR_KINDS.NOT_FOUND;
  if (status === 401 || status === 403) return ERROR_KINDS.AUTH;
  if (status === 400 || status === 422) return ERROR_KINDS.VALIDATION;
  if (status === 408) return ERROR_KINDS.TIMEOUT;
  if (status === 429) return ERROR_KINDS.RATE_LIMIT;
  if (status >= 500) return ERROR_KINDS.SERVER;
  return ERROR_KINDS.UNKNOWN;
}

// User-facing (English, matches site language). Validation errors usually
// carry a meaningful server message, so prefer that one.
export function friendlyMessage(error) {
  const kind = error instanceof ApiError ? error.kind : ERROR_KINDS.UNKNOWN;
  switch (kind) {
    case ERROR_KINDS.NOT_FOUND:
      return "We couldn't find what you were looking for.";
    case ERROR_KINDS.AUTH:
      return "You don't have permission to do this. Try logging in again.";
    case ERROR_KINDS.VALIDATION:
      return error.message || 'Something in the request was invalid.';
    case ERROR_KINDS.RATE_LIMIT:
      return "You're going a bit fast. Please wait a moment and try again.";
    case ERROR_KINDS.TIMEOUT:
      return 'The server took too long to respond. Please try again.';
    case ERROR_KINDS.OFFLINE:
      return "You appear to be offline. Check your connection and try again.";
    case ERROR_KINDS.NETWORK:
      return "We couldn't reach the server. Please try again.";
    case ERROR_KINDS.SERVER:
      return 'Something went wrong on our side. Please try again in a moment.';
    default:
      return error?.message || 'Something went wrong. Please try again.';
  }
}

const DEFAULT_TIMEOUT_MS = 15000;
const CSRF_COOKIE = 'bpb_csrf';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function browserCookie(name) {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const value = document.cookie.split(';').map((entry) => entry.trim()).find((entry) => entry.startsWith(prefix));
  if (!value) return null;
  try {
    return decodeURIComponent(value.slice(prefix.length));
  } catch {
    return null;
  }
}

// fetch-wrapper met timeout; mapt netwerk-failures naar getypeerde ApiErrors.
// Een door de caller meegegeven AbortSignal (bijv. superseded search) blijft
// gewoon als AbortError doorbubbelen.
export async function apiFetch(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const outer = options.signal;
  if (outer) {
    if (outer.aborted) controller.abort(outer.reason);
    else outer.addEventListener('abort', () => controller.abort(outer.reason), { once: true });
  }
  try {
    const browser = typeof window !== 'undefined';
    const method = String(options.method || 'GET').toUpperCase();
    const headers = new Headers(options.headers || {});
    if (browser && !SAFE_METHODS.has(method) && !headers.has('X-CSRF-Token')) {
      const csrfToken = browserCookie(CSRF_COOKIE);
      if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
    }
    return await fetch(url, {
      ...options,
      // Required for local development where :5183/:5184 calls :3001.
      // Same-origin Docker/production requests are unaffected.
      ...(browser && { credentials: options.credentials || 'include' }),
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err.name === 'AbortError' || err.name === 'TimeoutError') {
      if (outer?.aborted) throw err; // caller-initiated abort, geen fout
      throw new ApiError('The request timed out.', { kind: ERROR_KINDS.TIMEOUT, url, cause: err });
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new ApiError('You appear to be offline.', { kind: ERROR_KINDS.OFFLINE, url, cause: err });
    }
    throw new ApiError('Could not reach the server.', { kind: ERROR_KINDS.NETWORK, url, cause: err });
  } finally {
    clearTimeout(timer);
  }
}

// Gooit een getypeerde ApiError voor non-OK responses; gebruikt de
// servermelding uit de body als die er is (error/message + details).
export async function ensureOk(response, fallbackMessage = 'Request failed') {
  if (response.ok) return response;
  let serverMessage = null;
  try {
    const data = await response.clone().json();
    serverMessage = data?.error || data?.message || null;
    if (serverMessage && data?.details) serverMessage = `${serverMessage}: ${data.details}`;
  } catch {
    // non-JSON body (bijv. HTML van een proxy) - val terug op fallbackMessage
  }
  throw new ApiError(serverMessage || fallbackMessage, {
    kind: kindFromStatus(response.status),
    status: response.status,
    url: response.url,
  });
}
